import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeTaskTimeDepartment } from '../utils/taskTimeConstants';
import { isStrategyCallDeliverableName } from '../utils/programPricing';

// Title fallbacks for calls that predate explicit department tagging.
const DEPARTMENT_CALL_PATTERNS = {
  sales: /sales/i,
  operations: /operations|\bops\b/i,
  finance: /financ/i,
  technology: /technology|tech[\s-]?strategy/i,
  hr: /\bhr\b|human[\s-]?resources/i,
  marketing: /marketing/i,
};

export function matchesDepartmentTitle(title, department) {
  if (!title) return false;
  const pattern = DEPARTMENT_CALL_PATTERNS[normalizeTaskTimeDepartment(department)];
  return pattern ? pattern.test(String(title)) : false;
}

function matchesDepartment(record, department, titleCandidates) {
  const target = normalizeTaskTimeDepartment(department);
  if (record?.department) {
    return normalizeTaskTimeDepartment(record.department) === target;
  }
  return (titleCandidates || []).some((t) => matchesDepartmentTitle(t, target));
}

function normalizeCallStatus(status) {
  if (!status || status === 'completed') return 'Completed';
  if (status === 'booked' || status === 'scheduled') return 'Booked';
  if (status === 'canceled' || status === 'cancelled') return 'Canceled';
  if (status === 'rescheduled') return 'Rescheduled';
  return 'Completed';
}

function parseDateValue(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function extractDepartmentCallsFromDeliverables(accountData, department) {
  const calls = [];
  const deliverables = accountData?.deliverables || {};
  Object.entries(deliverables).forEach(([, delivData]) => {
    const name = (delivData?.name || '').toLowerCase();
    if (!isStrategyCallDeliverableName(name) && !name.includes('1:1')) return;
    (delivData.logs || []).forEach((log, index) => {
      const title = log.eventName || log.title || log.name || delivData.name || 'Strategy Call';
      if (department && !matchesDepartment(log, department, [title, log.calendlyEventUri])) return;
      const date = parseDateValue(log.scheduledStart || log.completedAt || log.date || log.createdAt);
      calls.push({
        id: `deliverable-${delivData.name}-${index}-${log.scheduledStart || index}`,
        title,
        date,
        status: normalizeCallStatus(log.meetingStatus),
        summary: log.summary || null,
        recordingUrl: log.recordingUrl || null,
        department: log.department || null,
        source: 'deliverable',
        eventId: log.googleEventId || log.eventId || null,
        completedWith: log.completedWith || null,
      });
    });
  });
  return calls;
}

/**
 * Strategy calls for one department: booked calls from deliverable logs merged
 * with completed Fathom meeting summaries. Explicit `department` fields win;
 * legacy records fall back to title matching. Pass a null department to get
 * every call for the account.
 */
export async function fetchDepartmentCalls(accountId, department = null) {
  if (!accountId) return [];

  const [accountSnap, summariesSnap] = await Promise.all([
    getDoc(doc(db, 'accounts', accountId)),
    getDocs(query(collection(db, 'meeting_summaries'), where('accountId', '==', accountId))),
  ]);

  const accountData = accountSnap.exists() ? accountSnap.data() : {};
  const fromDeliverables = extractDepartmentCallsFromDeliverables(accountData, department);

  const fromSummaries = summariesSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => !department || matchesDepartment(s, department, [s.meetingTitle]))
    .map((s) => ({
      id: s.id,
      title: s.meetingTitle || 'Strategy Call',
      date: parseDateValue(s.scheduledStartTime || s.createdAt),
      status: 'Completed',
      summary: s.summary || s.transcription || null,
      recordingUrl: s.recordingUrl || s.shareUrl || null,
      actionItems: s.actionItems || [],
      department: s.department || null,
      source: 'meeting_summary',
      eventId: s.eventId || s.googleEventId || null,
      recordedByEmail: s.recordedByEmail || null,
      inviteeEmails: Array.isArray(s.inviteeEmails) ? s.inviteeEmails : [],
    }));

  const merged = [...fromDeliverables, ...fromSummaries];
  const seen = new Set();
  const unique = merged.filter((call) => {
    const key = `${call.title}-${call.date?.toISOString?.() || call.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  unique.sort((a, b) => {
    const aMs = a.date?.getTime?.() || 0;
    const bMs = b.date?.getTime?.() || 0;
    return bMs - aMs;
  });

  return unique;
}

// All calls for an account across every department (portal "My Calls" view).
export function fetchAccountCalls(accountId) {
  return fetchDepartmentCalls(accountId, null);
}

export function countCompletedCalls(calls) {
  return calls.filter((c) => c.status === 'Completed').length;
}
